/**
 * Utility functions for camera handling and QR code scanning
 */

import { logger } from "../../../../utils/logger";

/**
 * Set up camera stream with specified constraints
 * @param {Function} onSuccess - Callback when camera is ready
 * @param {Function} onError - Callback when camera setup fails
 * @returns {Promise} Camera setup promise
 */
export const setupCamera = async (onSuccess, onError) => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: "environment" },
        width: { ideal: 1280, min: 640 },
        height: { ideal: 720, min: 480 },
      },
    });

    onSuccess(stream);
    return stream;
  } catch (err) {
    onError(err.message || "Camera access denied");
    logger.error("Camera error:", err);
    throw err;
  }
};

/**
 * Clean up camera stream and stop all tracks
 * @param {string} selector - CSS selector for video element
 */
export const cleanupCamera = (selector = "#qr-reader video") => {
  const videoEl = document.querySelector(selector);
  if (videoEl && videoEl.srcObject) {
    const stream = videoEl.srcObject;
    if (stream) {
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
    }
  }
};

/**
 * Load jsQR library dynamically with fallback
 * @returns {Promise} Promise that resolves when jsQR is loaded
 */
export const loadJsQRLibrary = () => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.jsQR) {
      resolve(window.jsQR);
      return;
    }

    const loadScript = (src, isRetry = false) => {
      const script = document.createElement("script");
      script.src = src;
      script.crossOrigin = "anonymous";

      script.onload = () => {
        if (window.jsQR) {
          resolve(window.jsQR);
        } else {
          reject(new Error("jsQR failed to load"));
        }
      };

      script.onerror = () => {
        logger.error(`Failed to load jsQR from ${src}`);
        if (!isRetry) {
          // Try alternative CDN
          setTimeout(() => {
            loadScript("https://unpkg.com/jsqr@1.4.0/dist/jsQR.min.js", true);
          }, 1000);
        } else {
          reject(new Error("Failed to load jsQR from all CDNs"));
        }
      };

      document.body.appendChild(script);
    };

    // Start with primary CDN
    loadScript("https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js");
  });
};

/**
 * Scan QR code from video element
 * @param {HTMLVideoElement} video - Video element
 * @param {HTMLCanvasElement} canvas - Canvas element for processing
 * @returns {Object|null} QR code data or null if not found
 */
export const scanQRFromVideo = (video, canvas) => {
  if (!video.videoWidth || !window.jsQR) return null;

  const ctx = canvas.getContext("2d", { willReadFrequently: true });

  // Use full video resolution for better detection
  if (video.videoWidth) {
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
  }

  // Draw video frame to canvas
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  const code = window.jsQR(imageData.data, imageData.width, imageData.height, {
    inversionAttempts: "dontInvert",
  });

  return code;
};

/**
 * Process QR code text content
 * @param {string} text - Raw QR code text
 * @returns {string} Cleaned QR code text
 */
export const processQRContent = (text) => {
  if (!text) return "";
  return text.replace(/(\r\n|\n|\r)/gm, "");
};
